/**
 * Правка названия задачи. Название видно в заголовке панели, а форма
 * открывается карандашом в шапке: править его может любой участник доски
 * с правом редактирования (как приоритет, тип, срок и статус).
 *
 * Поле — многострочное и растёт по содержимому: название до пятисот
 * символов в строку ввода не помещается, и в однострочном поле его
 * приходилось прокручивать вслепую. Переносов строк в названии при этом
 * не бывает — Enter сохраняет, как в обычном поле.
 */

import { useLayoutEffect, useRef, useState } from "react";

import type { WsError } from "@taskflow/client-core";
import { Button, FormField, Textarea, toast } from "@/shared/ui";

import { type TaskDetail, useUpdateTaskMutation } from "@taskflow/client-core/tasks";

export function TaskTitleEditor({ task, onDone }: { task: TaskDetail; onDone: () => void }) {
  const [update, { isLoading }] = useUpdateTaskMutation();
  const [draft, setDraft] = useState(task.title);
  const [error, setError] = useState<string | null>(null);
  const field = useRef<HTMLTextAreaElement>(null);

  // Высота по содержимому. CSS-свойство field-sizing поддержано не всеми
  // браузерами, а название обязано быть видно целиком в любом.
  useLayoutEffect(() => {
    const element = field.current;
    if (element === null) return;
    element.style.height = "auto";
    element.style.height = `${String(element.scrollHeight)}px`;
  }, [draft]);

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    const title = draft.trim();
    if (title === "") {
      setError("Укажите название задачи");
      return;
    }
    if (title === task.title) {
      onDone();
      return;
    }
    try {
      await update({ key: task.key, version: task.version, title }).unwrap();
      onDone();
    } catch (raw) {
      const wsError = raw as WsError;
      if (wsError.status === 409) {
        toast.warning("Задача изменилась", "Кто-то отредактировал её параллельно. Обновите панель.");
      } else {
        setError(wsError.data.fieldErrors?.title ?? wsError.data.message);
      }
    }
  };

  return (
    <form className="flex flex-col gap-2 px-5 py-4" onSubmit={(event) => void save(event)} noValidate>
      <FormField label="Название" required error={error}>
        {(props) => (
          <Textarea
            {...props}
            ref={field}
            value={draft}
            rows={1}
            maxLength={500}
            autoFocus
            className="resize-none overflow-hidden"
            // Перевод строки в названии не хранится: вставленный из письма
            // или таблицы текст схлопывается в пробелы, а не уезжает
            // невидимой второй строкой.
            onChange={(event) => setDraft(event.target.value.replace(/\s*\n+\s*/gu, " "))}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                void save(event);
              }
            }}
          />
        )}
      </FormField>
      <div className="flex gap-2">
        <Button size="sm" type="submit" loading={isLoading}>
          Сохранить
        </Button>
        <Button size="sm" variant="secondary" onClick={onDone}>
          Отмена
        </Button>
      </div>
    </form>
  );
}
